import { ForwardedRef, useCallback, useId, useRef, useEffect } from "react";
import { useSelect } from "downshift";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CoreSelect } from "@boi/components-core";
import { forwardRefGenerics } from "@boi/react-utils";

import * as S from "./Select.styled";
import { MAX_LIST_HEIGHT } from "./Select.constants";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { SelectBase, type SelectBaseProps } from "../SelectBase";
import { Typography } from "../Typography";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type SelectProps<
  T extends CoreSelect.SelectItem = CoreSelect.SelectItem
> = Omit<
  SelectBaseProps<T>,
  | "toggleButtonProps"
  | "getToggleButtonProps"
  | "getMenuProps"
  | "isOpen"
  | "renderButtonContent"
  | "renderListItem"
  | "maxListHeight"
> & {
  labelProps: Omit<
    InputLabelTopProps,
    "id" | "htmlFor" | "onClick" | "labelHintID" | "children"
  >;
  placeholder?: string;
  selectedItem?: T | null | undefined;
  onChange: (selectedItem: T | null | undefined) => void;
  alertProps?: InputAlertsProps["alerts"][number] | InputAlertsProps["alerts"];
};

function Select<T extends CoreSelect.SelectItem = CoreSelect.SelectItem>(
  props: SelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    labelProps,
    placeholder = "Select",
    selectedItem,
    onChange,
    alertProps,
    testID = "select",
    ...selectBaseProps
  } = props;

  const { isDisabled, items } = selectBaseProps;

  const { tokens } = useDesignTokens();
  const { select } = tokens;

  /**
   * This solution help to solve a MAT-21237 A11Y issue
   * In web version on mobile with voiceover the select field is not working properly without this.
   */
  const lastSelectedItemRef = useRef<T | null | undefined>(selectedItem);

  // Ensure the ref stays in sync with the selectedItem prop
  useEffect(() => {
    lastSelectedItemRef.current = selectedItem;
  }, [selectedItem]);

  const handleSelectedItemChange = useCallback(
    ({ selectedItem }: { selectedItem: T | null | undefined }) => {
      if (lastSelectedItemRef.current !== selectedItem) {
        lastSelectedItemRef.current = selectedItem;
        onChange(selectedItem);
      }
    },
    [onChange]
  );

  const {
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    isOpen,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items,
    selectedItem,
    itemToString: CoreSelect.itemToString,
    onSelectedItemChange: handleSelectedItemChange,
  });

  const labelHintID = useId();
  const { labelHint } = labelProps;
  const safeLabelHintID = labelHint ? labelHintID : undefined;

  const alertId = useId();
  const ariaAlertAssociationProps = buildAlertAssociationProps(
    alertId,
    props,
    safeLabelHintID
  );

  return (
    <div ref={ref}>
      <InputWrapper
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <InputLabelTop
          {...labelProps}
          {...getLabelProps()}
          labelHintID={labelHintID}
        >
          <SelectBase
            {...selectBaseProps}
            toggleButtonProps={{
              "aria-describedby": safeLabelHintID,
            }}
            getToggleButtonProps={getToggleButtonProps}
            getMenuProps={getMenuProps}
            isOpen={isOpen}
            renderButtonContent={() => (
              <S.ButtonTextWrapper
                $isDisabled={isDisabled}
                $hasSelectedItem={Boolean(selectedItem)}
              >
                <Typography
                  component="div"
                  color="inherit"
                  variant={select.button.text.typography.variant}
                  fontFamily={select.button.text.typography.fontFamily}
                  noWrap={true}
                >
                  {selectedItem ? selectedItem.title : placeholder}
                </Typography>
              </S.ButtonTextWrapper>
            )}
            renderListItem={(item, index) => {
              const isSelected = selectedItem === item;
              const isHighlighted = highlightedIndex === index;

              return (
                <S.ListItem
                  key={item.id}
                  {...getItemProps({ item, index })}
                  data-hovered={isHighlighted || undefined}
                  // HACK: this tabIndex=-1 makes talkback in chrome work
                  // Chromium issue: https://issues.chromium.org/issues/40260928
                  tabIndex={-1}
                >
                  <S.ListItemContentWrapper
                    $isSelected={isSelected}
                    $isHighlighted={isHighlighted}
                  >
                    <S.ListItemContent>
                      <S.ListItemTickIconWrapper>
                        {isSelected && (
                          <S.ListItemTickIcon aria-hidden="true" />
                        )}
                      </S.ListItemTickIconWrapper>

                      <Typography
                        component="div"
                        color="inherit"
                        title={item.title}
                        variant={
                          select.menu.option.inner.text.typography.variant
                        }
                        fontFamily={
                          select.menu.option.inner.text.typography.fontFamily
                        }
                      >
                        {item.title}
                      </Typography>
                    </S.ListItemContent>
                  </S.ListItemContentWrapper>
                </S.ListItem>
              );
            }}
            maxListHeight={MAX_LIST_HEIGHT}
            testID={testID}
            alertAssociationProps={ariaAlertAssociationProps}
          />
        </InputLabelTop>
      </InputWrapper>
    </div>
  );
}

export default forwardRefGenerics(Select);
